import { Document, parse, parseDocument, stringify } from 'yaml'
import { FlowSchema } from './flow.js'

/**
 * Parse and validate a Flow YAML string into a plain JS object.
 * For validation, runtime use, and tests that only care about data.
 *
 * @param {string} yamlString
 * @returns {object}
 * @throws {Error|ZodError} when YAML or schema validation fails
 */
export function parseFlow(yamlString) {
  const doc = parseDocument(yamlString, { strict: true, version: '1.2' })
  if (doc.errors.length > 0) {
    const err = new Error(`YAML parse error: ${doc.errors[0].message}`)
    err.cause = doc.errors
    throw err
  }
  const raw = doc.toJS()
  return FlowSchema.parse(raw)
}

/**
 * Serialize a Flow back to canonical YAML.
 * Input is plain data — comments are not preserved (there are none to preserve).
 * For comment-preserving operations, use serializeFlowDoc instead.
 *
 * @param {object} flow
 * @returns {string}
 */
export function serializeFlow(flow) {
  const validated = FlowSchema.parse(flow)
  return stringify(validated, {
    defaultKeyType: 'PLAIN',
    defaultStringType: 'PLAIN',
    lineWidth: 0,
  })
}

/**
 * Parse a Flow YAML into a Document (CST) for comment + key-order preserving
 * operations. The returned Document validates against the schema; mutate via
 * Document.setIn / Document.deleteIn and serialize with serializeFlowDoc.
 *
 * Used by `cairn accept` (patch suggestions applied while preserving user
 * comments) and the future visual editor's YAML round-trip path.
 *
 * @param {string} yamlString
 * @returns {Document}
 */
export function parseFlowDoc(yamlString) {
  const doc = parseDocument(yamlString, { strict: true, version: '1.2' })
  if (doc.errors.length > 0) {
    const err = new Error(`YAML parse error: ${doc.errors[0].message}`)
    err.cause = doc.errors
    throw err
  }
  // Validate the data shape but keep the CST.
  FlowSchema.parse(doc.toJS())
  return doc
}

/**
 * Serialize a Flow Document back to YAML preserving comments and key order.
 *
 * @param {Document} doc
 * @returns {string}
 */
export function serializeFlowDoc(doc) {
  // Validate the data shape one more time before writing.
  FlowSchema.parse(doc.toJS())
  return doc.toString({ lineWidth: 0 })
}

/**
 * Validate without throwing; returns a structured result.
 *
 * @param {string} yamlString
 * @returns {{ ok: true, flow: object } | { ok: false, errors: Array<{ message: string, path: Array<string|number>, line?: number, col?: number, code?: string }> }}
 */
export function validateFlow(yamlString) {
  let doc
  try {
    doc = parseDocument(yamlString, { strict: true, version: '1.2' })
  } catch (err) {
    return {
      ok: false,
      errors: [{ message: err instanceof Error ? err.message : String(err), path: [] }],
    }
  }
  if (doc.errors.length > 0) {
    return {
      ok: false,
      errors: doc.errors.map((e) => ({
        message: e.message,
        line: e.linePos?.[0]?.line,
        col: e.linePos?.[0]?.col,
        path: [],
      })),
    }
  }
  const raw = doc.toJS()
  const result = FlowSchema.safeParse(raw)
  if (result.success) {
    return { ok: true, flow: result.data }
  }
  return {
    ok: false,
    errors: result.error.issues.map((issue) => ({
      message: issue.message,
      path: issue.path,
      code: issue.code,
    })),
  }
}

export { parse as parseYamlRaw, stringify as stringifyYamlRaw, Document }
